import {
  BRICK_YARDS,
  ENDZONE_YARDS,
  FIELD_YARDS,
  PULL_YARDS,
} from "./FieldCanvas";
import { GamePoint } from "./GamePoint";

export type ApiGameEvents = {
  homeEvents: any[];
  awayEvents: any[];
};

export type GameEvent = {
  teamId: string;
  eventType: GameEventType;
  details: any;
  timestamp?: string;

  // for start of point events.
  line?: string[];
  time?: number;

  // For pull events.
  puller?: string;
  pullX?: number;
  pullY?: number;

  // For pass and goal events.
  thrower?: string;
  throwerX?: number;
  throwerY?: number;
  receiver?: string;
  receiverX?: number;
  receiverY?: number;

  // For block events.
  defender?: string;

  // For turnover events.
  turnoverX?: number;
  turnoverY?: number;
};

export enum GameEventType {
  START_D_POINT = 1,
  START_O_POINT = 2,
  MIDPOINT_TIMEOUT_RECORDING_TEAM = 3,
  BETWEEN_POINT_TIMEOUT_RECORDING_TEAM = 4,
  MIDPOINT_TIMEOUT_OPPOSING_TEAM = 5,
  BETWEEN_POINT_TIMEOUT_OPPOSING_TEAM = 6,
  PULL_INBOUNDS = 7,
  PULL_OUT_OF_BOUNDS = 8,
  OFFSIDES_RECORDING_TEAM = 9,
  OFFSIDES_OPPOSING_TEAM = 10,
  BLOCK = 11,
  CALLAHAN_THROWN_BY_OPPOSING_TEAM = 12,
  THROWAWAY_THROWN_BY_OPPOSING_TEAM = 13,
  STALL_AGAINST_OPPOSING_TEAM = 14,
  SCORE_BY_OPPOSING_TEAM = 15,
  PENALTY_ON_RECORDING_TEAM = 16,
  PENALTY_ON_OPPOSING_TEAM = 17,
  PASS = 18,
  GOAL = 19,
  DROP = 20,
  DROPPED_PULL = 21,
  THROWAWAY_THROWN_BY_RECORDING_TEAM = 22,
  CALLAHAN_THROWN_BY_RECORDING_TEAM = 23,
  STALL_AGAINST_RECORDING_TEAM = 24,
  INJURY = 25,
  PLAYER_MISCONDUCT_FOUL = 26,
  PLAYER_EJECTED = 27,
  END_OF_FIRST_QUARTER = 28,
  HALFTIME = 29,
  END_OF_THIRD_QUARTER = 30,
  END_OF_REGULATION = 31,
  END_OF_FIRST_OVERTIME = 32,
  END_OF_SECOND_OVERTIME = 33,
  DELAYED = 34,
  POSTPONED = 35,
}

export function getGameEventType(eventType: number): GameEventType {
  if (typeof eventType === "number" && eventType >= 1 && eventType <= 35) {
    return eventType as GameEventType;
  }
  throw new Error(`Unknown event type: ${eventType}`);
}

export function isPull(gameEvent: GameEvent): boolean {
  const gameEventType = gameEvent.eventType;
  return (
    gameEventType === GameEventType.PULL_INBOUNDS ||
    gameEventType === GameEventType.PULL_OUT_OF_BOUNDS
  );
}

export function isGoal(gameEvent: GameEvent): boolean {
  const gameEventType = gameEvent.eventType;
  return (
    gameEventType === GameEventType.GOAL ||
    gameEventType === GameEventType.CALLAHAN_THROWN_BY_OPPOSING_TEAM ||
    gameEventType === GameEventType.CALLAHAN_THROWN_BY_RECORDING_TEAM
  );
}

export function isTurnover(gameEvent: GameEvent): boolean {
  const gameEventType = gameEvent.eventType;
  return (
    gameEventType === GameEventType.BLOCK ||
    gameEventType === GameEventType.CALLAHAN_THROWN_BY_OPPOSING_TEAM ||
    gameEventType === GameEventType.THROWAWAY_THROWN_BY_OPPOSING_TEAM ||
    gameEventType === GameEventType.STALL_AGAINST_OPPOSING_TEAM ||
    gameEventType === GameEventType.PENALTY_ON_RECORDING_TEAM ||
    gameEventType === GameEventType.DROP ||
    gameEventType === GameEventType.DROPPED_PULL ||
    gameEventType === GameEventType.THROWAWAY_THROWN_BY_RECORDING_TEAM ||
    gameEventType === GameEventType.CALLAHAN_THROWN_BY_RECORDING_TEAM ||
    gameEventType === GameEventType.STALL_AGAINST_RECORDING_TEAM
  );
}

export function isTakeaway(gameEvent: GameEvent): boolean {
  const gameEventType = gameEvent.eventType;
  return (
    gameEventType === GameEventType.BLOCK ||
    gameEventType === GameEventType.CALLAHAN_THROWN_BY_OPPOSING_TEAM ||
    gameEventType === GameEventType.STALL_AGAINST_OPPOSING_TEAM
  );
}

export function isPass(gameEvent: GameEvent): boolean {
  const gameEventType = gameEvent.eventType;
  return gameEventType === GameEventType.PASS;
}

export function isPenalty(gameEvent: GameEvent): boolean {
  const gameEventType = gameEvent.eventType;
  return (
    gameEventType === GameEventType.PENALTY_ON_RECORDING_TEAM ||
    gameEventType === GameEventType.PENALTY_ON_OPPOSING_TEAM
  );
}

export function clockExpires(gameEvent: GameEvent): boolean {
  const gameEventType = gameEvent.eventType;
  return (
    gameEventType === GameEventType.END_OF_FIRST_QUARTER ||
    gameEventType === GameEventType.HALFTIME ||
    gameEventType === GameEventType.END_OF_THIRD_QUARTER ||
    gameEventType === GameEventType.END_OF_REGULATION ||
    gameEventType === GameEventType.END_OF_FIRST_OVERTIME ||
    gameEventType === GameEventType.END_OF_SECOND_OVERTIME
  );
}

export function endsPoint(gameEvent: GameEvent): boolean {
  return isGoal(gameEvent) || clockExpires(gameEvent);
}

export function isPointInitiatingAction(gameEvent: GameEvent): boolean {
  const gameEventType = gameEvent.eventType;
  return (
    gameEventType === GameEventType.PULL_INBOUNDS ||
    gameEventType === GameEventType.PULL_OUT_OF_BOUNDS ||
    gameEventType === GameEventType.OFFSIDES_OPPOSING_TEAM ||
    gameEventType === GameEventType.OFFSIDES_RECORDING_TEAM
  );
}

export function findCoords(
  gamePoint: GamePoint,
  gameEvent: GameEvent,
): {
  startCoord: { x: number; y: number } | null;
  endCoord: { x: number; y: number } | null;
} {
  const rawCoords = findRawCoords(gameEvent);
  if (gameEvent.teamId === gamePoint.receivingTeam) {
    return rawCoords;
  }
  const updatedStartCoord = rawCoords.startCoord
    ? {
        x: -rawCoords.startCoord.x,
        y: FIELD_YARDS - rawCoords.startCoord.y,
      }
    : null;
  const updatedEndCoord = rawCoords.endCoord
    ? {
        x: -rawCoords.endCoord.x,
        y: FIELD_YARDS - rawCoords.endCoord.y,
      }
    : null;
  return {
    startCoord: updatedStartCoord,
    endCoord: updatedEndCoord,
  };
}

function findRawCoords(gameEvent: GameEvent): {
  startCoord: { x: number; y: number } | null;
  endCoord: { x: number; y: number } | null;
} {
  if (isPull(gameEvent)) {
    return {
      startCoord: {
        x: 0,
        y: ENDZONE_YARDS + PULL_YARDS,
      },
      endCoord: {
        x: gameEvent.pullX || 0,
        y: gameEvent.pullY || FIELD_YARDS - ENDZONE_YARDS - BRICK_YARDS,
      },
    };
  } else if (gameEvent.eventType === GameEventType.SCORE_BY_OPPOSING_TEAM) {
    return {
      startCoord: null,
      endCoord: null,
    };
  } else if (
    isPass(gameEvent) ||
    isGoal(gameEvent) ||
    gameEvent.eventType === GameEventType.DROP
  ) {
    return {
      startCoord: {
        x: gameEvent.throwerX || 0,
        y: gameEvent.throwerY || 0,
      },
      endCoord: {
        x: gameEvent.receiverX || 0,
        y: gameEvent.receiverY || 0,
      },
    };
  } else if (
    gameEvent.eventType === GameEventType.THROWAWAY_THROWN_BY_RECORDING_TEAM
  ) {
    return {
      startCoord: {
        x: gameEvent.throwerX || 0,
        y: gameEvent.throwerY || 0,
      },
      endCoord: {
        x: gameEvent.turnoverX || 0,
        y: gameEvent.turnoverY || 0,
      },
    };
  }

  return {
    startCoord: null,
    endCoord: null,
  };
}
